import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { POLL_MAX_OPTIONS, POLL_MIN_OPTIONS, pollDraftProblem, type PollDraft } from '../lib/polls'
import { useT } from '../lib/i18n'
import { IconButton } from './IconButton'
import { CloseIcon, PlusIcon, TrashIcon } from './Icons'
import { Spinner } from './Spinner'

type PollDialogProps = {
  onClose: () => void
  /**
   * Persist the draft (`createPoll`). Resolving closes the dialog through the caller; a rejection
   * keeps it up with every field intact, so a failed send is one press away from a retry.
   */
  onSend: (draft: PollDraft) => Promise<void>
}

const QUESTION_ID = 'poll-question'
const PROBLEM_ID = 'poll-problem'
const OPTION_ID_PREFIX = 'poll-option-'

/**
 * "New poll" (W11) — the composer's one attach surface that is not an upload. The shape is the
 * app's other dialogs' (ForwardPicker, the pickers): bottom sheet on a phone, centred panel on
 * desktop, focus on open, Escape closes, Tab is kept inside, the scrim cancels — until the send is
 * in flight, when it stays up for its answer.
 *
 * The list is 2–10 fields by construction: Add disables itself at ten and Remove at two, so the
 * count can never be wrong. What *can* be wrong — a blank question, a blank option, an option past
 * the api's 80 characters — is left in the writer's hands and said out loud: Send is disabled and
 * the line above it carries `pollDraftProblem`'s reason, in the api's own order, so the sentence
 * shown is always the one the api would give. A disabled control with no reason is a dead end; this
 * one always names the way out.
 */
export function PollDialog({ onClose, onSend }: PollDialogProps) {
  const t = useT()
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState<string[]>(['', ''])
  const [multiple, setMultiple] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const questionRef = useRef<HTMLTextAreaElement>(null)
  /** The option field just added, which takes the focus once it exists; null when there is none. */
  const focusOption = useRef<number | null>(null)

  const problem = pollDraftProblem({ question, options })

  useEffect(() => {
    questionRef.current?.focus()
  }, [])

  useLayoutEffect(() => {
    const index = focusOption.current
    if (index === null) return
    focusOption.current = null
    // A new field is one more place to write, so it is where the caret goes — the Add control is
    // below the list and Tab would walk the old fields again to reach it.
    dialogRef.current?.querySelector<HTMLInputElement>(`[data-poll-option="${index}"]`)?.focus()
  }, [options])

  useEffect(() => {
    const dialog = dialogRef.current
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Not while the send is in flight: the dialog stays up until its answer lands, so a
        // dismissal cannot leave a request nobody is waiting for.
        if (!busy) onClose()
        return
      }
      if (event.key !== 'Tab' || !dialog) return
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  const addOption = () => {
    if (options.length >= POLL_MAX_OPTIONS) return
    focusOption.current = options.length
    setOptions((previous) => [...previous, ''])
  }

  const send = async () => {
    if (problem !== null || busy) return
    setBusy(true)
    setError(null)
    try {
      // The api trims what it stores; trimming here too keeps the optimistic copy and the stored
      // row the same string, and the bounds `pollDraftProblem` measured are the trimmed ones.
      await onSend({ question: question.trim(), options: options.map((option) => option.trim()), multiple })
    } catch {
      setError(t('composer.messageNotSent'))
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-app/80 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="poll-heading"
        tabIndex={-1}
        className="sheet-enter flex max-h-[85dvh] w-full max-w-md flex-col rounded-t-panel bg-surface pb-[var(--sab)] outline-none shadow-lift ring-1 ring-line-strong sm:rounded-panel sm:pb-0"
      >
        <header className="flex items-center gap-2 border-b border-line px-3 py-2.5">
          <h2 id="poll-heading" className="min-w-0 flex-1 truncate text-title font-semibold text-ink">
            {t('poll.title')}
          </h2>
          <IconButton label={t('chat.close')} size={36} disabled={busy} onClick={onClose}>
            <CloseIcon size={18} />
          </IconButton>
        </header>

        <div className="chat-scroll min-h-0 flex-1 p-3">
          <label htmlFor={QUESTION_ID} className="block text-meta font-semibold text-muted">
            {t('poll.question')}
          </label>
          {/* The question is a body like any message's, and it arrives as one in `lib/data.ts`; the
              field mirrors the composer's, `dir="auto"` included, so an Arabic question is typed
              the way it will be read. */}
          <textarea
            id={QUESTION_ID}
            ref={questionRef}
            value={question}
            rows={2}
            dir="auto"
            disabled={busy}
            onChange={(event) => setQuestion(event.target.value)}
            className="mt-1 block w-full resize-none rounded-well bg-surface2 px-3 py-2.5 text-body text-ink ring-1 ring-line ring-inset focus-visible:ring-accent/70"
          />

          <ul className="mt-3 flex flex-col gap-2">
            {options.map((option, index) => {
              const number = index + 1
              const id = `${OPTION_ID_PREFIX}${index}`
              const label = t('poll.option', { number })
              return (
                <li key={id} className="flex items-center gap-1.5">
                  {/* The number is the field's visible label; the name a screen reader hears is the
                      `sr-only` one, which says the word too. */}
                  <span aria-hidden="true" className="numeric w-4 shrink-0 text-center text-meta text-muted">
                    {number}
                  </span>
                  <label htmlFor={id} className="sr-only">
                    {label}
                  </label>
                  <input
                    id={id}
                    data-poll-option={index}
                    value={option}
                    dir="auto"
                    placeholder={label}
                    disabled={busy}
                    onChange={(event) => {
                      const value = event.target.value
                      setOptions((previous) =>
                        previous.map((entry, at) => (at === index ? value : entry)),
                      )
                    }}
                    className="min-h-11 min-w-0 flex-1 rounded-well bg-surface2 px-3 text-ui text-ink ring-1 ring-line ring-inset placeholder:text-muted focus-visible:ring-accent/70"
                  />
                  <IconButton
                    label={t('poll.removeOption', { number })}
                    size={36}
                    disabled={busy || options.length <= POLL_MIN_OPTIONS}
                    onClick={() => setOptions((previous) => previous.filter((_, at) => at !== index))}
                  >
                    <TrashIcon size={16} />
                  </IconButton>
                </li>
              )
            })}
          </ul>

          <button
            type="button"
            disabled={busy || options.length >= POLL_MAX_OPTIONS}
            onClick={addOption}
            className="mt-2 inline-flex min-h-11 items-center gap-2 rounded-full px-3 text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 disabled:opacity-40"
          >
            <PlusIcon size={16} />
            {t('poll.addOption')}
          </button>

          <label className="mt-3 flex min-h-11 cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              className="size-5 shrink-0 accent-accent"
              checked={multiple}
              disabled={busy}
              onChange={(event) => setMultiple(event.target.checked)}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-ui text-ink">{t('poll.multiple')}</span>
              <span className="block text-meta text-muted">{t('poll.multipleHint')}</span>
            </span>
          </label>
        </div>

        <div className="border-t border-line">
          {/* One line. The draft's problem wins while it exists: it is what disables Send, it is the
              line `aria-describedby` points the button at, and it is about *this* draft — a failure
              from an earlier attempt says nothing about the words on screen now. It is a `status`
              rather than an `alert`: it changes as the writer fixes the draft, so it does not shout
              on every keystroke; the refusal below is the one thing that is an alert. */}
          {problem !== null ? (
            <p id={PROBLEM_ID} role="status" className="px-4 pt-2.5 text-meta text-muted">
              {t(problem)}
            </p>
          ) : error !== null ? (
            <p role="alert" className="px-4 pt-2.5 text-meta text-danger">
              {error}
            </p>
          ) : null}
          <div className="flex items-center gap-2 p-3">
            <button
              type="button"
              disabled={busy}
              onClick={onClose}
              className="min-h-11 flex-1 rounded-full text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 disabled:opacity-50"
            >
              {t('chat.cancel')}
            </button>
            <button
              type="button"
              disabled={problem !== null || busy}
              aria-describedby={problem !== null ? PROBLEM_ID : undefined}
              onClick={() => void send()}
              className="press flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-accent px-4 text-ui font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-40"
            >
              {busy && <Spinner size={16} label={t('poll.send')} />}
              {t('poll.send')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
